import { useMemo, useState } from 'react';
import { ratesFor } from '../rules/tax';
import {
  moneyExact, moneyExactSigned, pct, price as fmtPrice, shares as fmtShares, sharesOf,
  shortDate, todayIso,
  isDay,
} from '../lib/format';
import { newId } from '../lib/id';
import { decimalOr } from '../lib/number';
import { sharesToReachCap, toUsd } from '../state/derive';
import { realisedAfterFee, saleProceeds } from '../state/commission';
import { doubtAboutPrice } from '../state/priceSanity';
import { cashCurrency, effectOf, moveCash, settle, tradeCashDelta } from '../state/cashMove';
import { accountName, isTaxShielded } from '../lib/accounts';
import { useStore } from '../state/store';
import type { Account, Trade, TradeReason } from '../types';
import type { Nav } from '../App';
import { HideToggle } from '../components/HideToggle';

const REASONS: Array<{ value: TradeReason; label: string; blurb: string }> = [
  { value: 'rebuild_cash', label: 'Rebuild cash', blurb: 'It sits as cash until something is worth buying.' },
  { value: 'rotate', label: 'Rotate into another name', blurb: 'It goes straight into something else.' },
  { value: 'thesis_broke', label: 'The thesis broke', blurb: 'What you said would prove you wrong happened.' },
];

export function ReduceClose({ ticker, nav }: { ticker: string; nav: Nav }) {
  const { book, state, limits, putHolding, removeHolding, putTrade, setCash } = useStore();
  const accounts = state.data.accounts;
  const currency = state.settings.displayCurrency;
  const fx = state.fx;
  const toDisplay = (usd: number) => (currency === 'PLN' && fx?.usdPln ? usd * fx.usdPln : usd);

  const p = book.positions.find((x) => x.ticker === ticker);

  // IKE is tax-shielded, so it is the last place to sell from. The default
  // account is the largest taxable lot (§7.4).
  const defaultAccount = useMemo(() => {
    if (!p) return 'revolut' as Account;
    const taxable = p.holdings.filter((h) => !isTaxShielded(accounts, h.account));
    const pool = taxable.length > 0 ? taxable : p.holdings;
    return [...pool].sort((a, b) => b.shares - a.shares)[0]!.account;
  }, [p, accounts]);

  const [account, setAccount] = useState<Account>(defaultAccount);
  const [sellShares, setSellShares] = useState(0);
  /* What is actually in the box, kept apart from the parsed figure. A field
     driven straight off the number cannot be typed into with a comma: the
     separator is parsed away and re-rendered gone before the next digit
     arrives, so "169,5" comes out 1695. */
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState<TradeReason | ''>('');
  const [destination, setDestination] = useState('');
  const [fee, setFee] = useState('');
  /* When it happened, not when it is being typed in. Every dated reading takes
     this: the markers on the price chart, the earliest-parcel-first matching on
     the tax screen, and the share count a dividend was paid on. Today is the
     default because most sales are recorded the day they happen. */
  const [at, setAt] = useState(todayIso());
  /* What the shares actually went for.
   *
   * This screen used to have no such field: it wrote `p.price` on to the trade,
   * meaning every recorded sale claimed to have happened at whatever the mark
   * was the moment the button was pressed. §1 is explicit that "sell" means
   * recording a sale already executed at the broker, and the price is the one
   * fact only the person who executed it has. Everything downstream reads off
   * it — the proceeds, the realised gain, the tax figure, the cash that moves —
   * so the whole screen was doing correct arithmetic on a number nobody
   * entered. Blank until the mark arrives, then filled with it as a starting
   * point rather than as an answer. */
  const [priceTyped, setPriceTyped] = useState('');
  const [priceTouched, setPriceTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!p) {
    return (
      <div className="screen">
        <div className="screen-head">
          <button className="back" onClick={nav.back} aria-label="Back">‹</button>
          <h1>{ticker}</h1>
          <HideToggle push />
        </div>
        <div className="empty">This position is no longer in the book.</div>
      </div>
    );
  }

  const lot = p.holdings.find((h) => h.account === account) ?? p.holdings[0]!;
  const maxShares = lot.shares;
  const listingCcy = (p.quote?.currency ?? p.currency) as 'USD';
  const priceUsd = toUsd(p.price, listingCcy, fx) ?? 0;

  /* Whether the proceeds can reach this account's balance at all. Crossing into
     an account held in another currency needs a rate, and with none the balance
     is deliberately left alone — worth saying before the sale rather than
     leaving somebody to find a sale that produced no cash. Probed with a unit
     amount, so it reads the same whatever is typed above. */
  const settlesIn = cashCurrency(book.cashByAccount, accounts, lot.account);
  const proceedsLand = settlesIn.toUpperCase() === listingCcy.toUpperCase()
    || moveCash(book.cashByAccount, accounts, lot.account, 1, listingCcy, fx) !== null;

  const clamped = Math.min(sellShares, maxShares);
  const feePaid = decimalOr(fee);
  /* The mark is the default and nothing more. Untouched, the field follows the
     price as it refreshes; once edited it stays put, because a figure somebody
     typed is not something to overwrite from the network. */
  const salePrice = priceTouched ? decimalOr(priceTyped) : p.price;
  const salePriceUsd = toUsd(salePrice, listingCcy, fx) ?? 0;
  const priceDoubt = doubtAboutPrice(salePrice, p.price, clamped);

  /* Two figures, not one. The position shrinks by the market value of the
     shares that left; the cash rises by what actually arrived, which is that
     less the commission. Using the gross for both made the book look as though
     it kept the fee: the hint under the fee field has always said the fee comes
     off the proceeds, while the Proceeds line above it was still gross. */
  /* The position shrinks by what the shares were worth on the market, because
     what is left is still marked to it. The cash rises by what the sale
     actually brought in, which is the entered price less the commission. When
     the two prices differ the gap is not lost: it is the difference between the
     book's mark and the fill, and it lands in the realised result where it
     belongs. */
  const soldValueUsd = clamped * priceUsd;
  const grossUsd = clamped * salePriceUsd;
  const proceedsUsd = toUsd(saleProceeds(clamped, salePrice, feePaid), listingCcy, fx) ?? 0;

  /* --- before / after, for the four numbers §7.4 asks for --- */
  // The money stays in the book, as cash — all of it except what the broker took.
  const totalAfter = book.totals.totalUsd - (soldValueUsd - proceedsUsd);
  const positionAfterUsd = p.marketValue - soldValueUsd;
  const weightAfter = totalAfter > 0 ? (positionAfterUsd / totalAfter) * 100 : 0;
  const cashAfterUsd = book.totals.cashUsd + proceedsUsd;
  const cashPctAfter = totalAfter > 0 ? (cashAfterUsd / totalAfter) * 100 : 0;

  const affectedThemes = book.themes.filter((t) => t.tickers.includes(p.ticker));
  const biggestTheme = affectedThemes[0] ?? null;
  const themeWeightAfter = biggestTheme
    ? biggestTheme.weight - (p.weight - weightAfter)
    : 0;

  /* Realized gain is measured against this lot's own cost, not the blended one
     — and net of what the broker takes to get out. The cost of getting in is
     already inside `avgCost`, so it is not subtracted a second time. */
  const costKnown = lot.avgCost > 0;
  const gainNative = costKnown ? realisedAfterFee(clamped, salePrice, lot.avgCost, feePaid) : 0;
  const gainUsd = costKnown ? toUsd(gainNative, listingCcy, fx) ?? 0 : 0;
  const shieldedFromTax = isTaxShielded(accounts, lot.account);
  /* The rate the user set under Taxes, not a constant. It was 19% in the
     source, which is one country's answer printed as though it were arithmetic.
     With no rate set there is no tax line rather than a line reading zero. */
  const taxRate = ratesFor(state.settings).gainsPct;
  const taxUsd = shieldedFromTax || gainUsd <= 0 || taxRate === null
    ? 0 : gainUsd * (taxRate / 100);

  const toLimit = p.cap !== null
    ? sharesToReachCap(p.marketValue, book.totals.totalUsd, priceUsd, p.cap)
    : 0;
  // "To your limit" is per-lot: it cannot sell more than this account holds.
  const toLimitHere = Math.min(toLimit, maxShares);
  // One rounding, used by both the hint and the button, so they never disagree.
  const toLimitRounded = Number(toLimit.toFixed(4));

  const rotatingIntoHotTheme =
    reason === 'rotate' && destination.trim() !== ''
      ? book.themes.find(
          (t) => t.weight > limits.themeLimit &&
            t.tickers.some((tk) => tk.toUpperCase() === destination.trim().toUpperCase()),
        ) ?? null
      : null;

  const commit = async () => {
    if (clamped <= 0 || saving) return;
    setSaving(true);
    const remaining = Number((lot.shares - clamped).toFixed(8));

    const trade: Trade = {
      id: newId('trade'),
      ticker: p.ticker,
      account: lot.account,
      side: 'sell',
      shares: clamped,
      price: salePrice,
      // Noon, so a date input's day is unambiguous wherever it is read back.
      at: `${at}T12:00:00.000Z`,
    };
    if (feePaid > 0) trade.fee = feePaid;
    if (costKnown) trade.realizedGain = gainNative;
    if (reason) trade.reason = reason;
    if (reason === 'rotate' && destination.trim()) trade.destination = destination.trim().toUpperCase();

    /* The proceeds land in the account they were sold from, and what they did
       is remembered on the trade.
       *
       * This screen used to write the balance directly and remember nothing,
       * which put every sale outside the one arrangement the rest of the book
       * runs on: the trade log would accept a correction to the price, or
       * delete the sale outright, and the cash would sit there ignoring both —
       * because `restating` and `reverse` have nothing to undo on a record that
       * never said what it did. Buying has recorded its effect since the day
       * buying started moving cash; selling is the half that was missed.
       *
       * It also crossed currencies by hand, and where it had no rate it used
       * one: a sale in dollars into an account held in anything but dollars or
       * złoty credited the dollar figure unconverted, and a missing rate
       * credited nothing at all without saying so. `moveCash` converts where it
       * can and refuses where it cannot, which is the rule everywhere else. */
    const proceeds = effectOf(moveCash(
      book.cashByAccount, accounts, lot.account,
      tradeCashDelta('sell', clamped, salePrice, feePaid), listingCcy, fx,
    ));
    if (proceeds) trade.cashEffect = proceeds;

    await putTrade(trade);

    if (remaining <= 0) await removeHolding(lot.id);
    else await putHolding({ ...lot, shares: remaining });

    for (const m of settle(book.cashByAccount, [proceeds])) {
      await setCash(m.account, m.amount, m.currency);
    }

    setSaving(false);
    nav.go({ name: 'value' });
  };

  /** Sets both the figure and the text, for everything that is not typing. */
  const setShares = (n: number) => {
    setSellShares(n);
    setTyped(n === 0 ? '' : String(Number(n.toFixed(8))));
  };

  const setPct = (fraction: number) => setShares(Number((maxShares * fraction).toFixed(8)));

  return (
    <div className="screen">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>Reduce {p.ticker}</h1>
        <HideToggle push />
      </div>

      <div className="card card-pad">
        <div className="field">
          <label htmlFor="acct">Which account did you sell from?</label>
          <select id="acct" className="select" value={account}
                  onChange={(e) => { setAccount(e.target.value as Account); setShares(0); }}>
            {p.holdings.map((h) => (
              <option key={h.id} value={h.account}>
                {accountName(accounts, h.account)} · {sharesOf(h.shares, p.ticker)}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ marginBottom: 6 }}>
          <label htmlFor="amount">How many shares</label>
          <input
            id="amount" className="input" data-mono="true" inputMode="decimal"
            value={typed}
            placeholder="0"
            onChange={(e) => {
              setTyped(e.target.value);
              setSellShares(Math.min(maxShares, decimalOr(e.target.value)));
            }}
          />
        </div>

        {/* The price it actually went for, which is the one fact on this screen
            that only you have. Prefilled with the mark, and the line under it
            answers the question the screen could not before: what the sale
            came to. */}
        <div className="field" style={{ marginBottom: 6 }}>
          <label htmlFor="sell-price">Price you sold at ({listingCcy})</label>
          <input
            id="sell-price" className="input" data-mono="true" inputMode="decimal"
            value={priceTouched ? priceTyped : (p.price > 0 ? String(p.price) : '')}
            placeholder={p.price > 0 ? String(p.price) : '0.00'}
            onChange={(e) => { setPriceTouched(true); setPriceTyped(e.target.value); }}
          />
          <div className="hint">
            {clamped > 0 && salePrice > 0
              ? <>{sharesOf(clamped, p.ticker)} at{' '}
                  <span className="num">{fmtPrice(salePrice)}</span> comes to{' '}
                  <span className="num">{moneyExact(toDisplay(grossUsd), currency)}</span>
                  {feePaid > 0 ? ', before the commission below.' : '.'}</>
              : <>The mark is used until you change it. It is a starting point, not the fill.</>}
          </div>
          {/* The same warning the buying screens carry, for the same slip: a
              total typed into a per-share field. It never refuses. */}
          {priceDoubt ? (
            <div className="note" data-tone="warn" style={{ marginTop: 8 }}>
              That is {priceDoubt.multiple.toFixed(1)}× the mark of {fmtPrice(p.price)}.
              {priceDoubt.totalLooksRight
                ? ` Read as the total for all ${fmtShares(clamped)}, it would be `
                  + `${fmtPrice(priceDoubt.ifTotal)} a share, which is about where the market is.`
                : ' A price far from today\u2019s mark is ordinary, so this is only worth a look.'}
            </div>
          ) : null}
        </div>

        <input
          className="slider" type="range" min={0} max={maxShares} step={maxShares / 1000}
          value={clamped}
          aria-label={`Shares to sell, up to ${fmtShares(maxShares)}`}
          style={{ ['--fill' as string]: `${(clamped / maxShares) * 100}%` }}
          onChange={(e) => setShares(Number(e.target.value))}
        />

        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" onClick={() => setPct(0.25)}>25%</button>
          <button className="btn btn-sm" onClick={() => setPct(0.5)}>50%</button>
          <button className="btn btn-sm" disabled={toLimitHere <= 0}
                  onClick={() => setShares(Number(Math.min(toLimitRounded, maxShares).toFixed(4)))}>
            To your limit
          </button>
          <button className="btn btn-sm" onClick={() => setPct(1)}>All</button>
        </div>

        {p.cap !== null && toLimit > 0 ? (
          <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
            {sharesOf(toLimitRounded, p.ticker)} brings it from {pct(p.weight)} to
            its {pct(p.cap)} ceiling
            {toLimit > maxShares
              ? `, which is more than this account holds, so you would need to sell from another account too.`
              : '.'}
          </div>
        ) : null}
      </div>

      {/* Live before / after (§7.4) */}
      <div className="card card-pad" style={{ marginTop: 10 }}>
        <div className="ba-grid">
          <BeforeAfter k={`${p.ticker} weight`} before={pct(p.weight)} after={pct(weightAfter)}
                       flag={p.cap !== null && weightAfter > p.cap} />
          <BeforeAfter k="Cash" before={pct(book.totals.cashPct)} after={pct(cashPctAfter)}
                       flag={cashPctAfter < (state.settings.cycleIsHot ? limits.cashFloorHot : limits.cashFloor)} />
          {biggestTheme ? (
            <BeforeAfter k={biggestTheme.name} before={pct(biggestTheme.weight)}
                         after={pct(themeWeightAfter)} flag={themeWeightAfter > limits.themeLimit} />
          ) : null}
          <BeforeAfter
            k="Realized gain"
            before={moneyExact(0, currency)}
            after={costKnown ? moneyExactSigned(toDisplay(gainUsd), currency) : 'unknown'}
            flag={false}
          />
        </div>

        <div className="divider" />

        {/* Tax line (§7.4) */}
        {costKnown ? (
          <div className="kv-list">
            <div className="kv">
              <span className="k">Gain</span>
              <span className={`v num ${gainUsd >= 0 ? 'up' : 'down'}`}>
                {moneyExactSigned(toDisplay(gainUsd), currency)}
              </span>
            </div>
            <div className="kv">
              <span className="k">
                {taxRate === null ? 'Tax' : `Tax at ${pct(taxRate)}`}
              </span>
              <span className="v num">
                {shieldedFromTax ? 'sheltered · outside the tax base'
                  : taxRate === null ? 'no rate set · see Taxes'
                  : moneyExact(toDisplay(taxUsd), currency)}
              </span>
            </div>
            <div className="kv">
              <span className="k">Proceeds</span>
              <span className="v num">{moneyExact(toDisplay(proceedsUsd), currency)}</span>
            </div>
          </div>
        ) : null}

        {!proceedsLand ? (
          <div className="note" data-tone="warn" style={{ marginTop: 12 }}>
            {accountName(accounts, lot.account)} is held in {settlesIn} and this sells in
            {' '}{listingCcy}, with no rate to cross them. The sale is recorded; its free cash is
            left where it is rather than moved at a rate the book does not have.
          </div>
        ) : null}

        {!costKnown ? (
          <div className="note">
            This lot has no cost recorded, so the realized gain and the tax on it cannot be worked out.
          </div>
        ) : null}

        <div className="note" data-tone={shieldedFromTax ? 'warn' : undefined} style={{ marginTop: 12 }}>
          {shieldedFromTax
            ? `You are selling out of ${accountName(accounts, lot.account)}. Gains there are shielded, which is exactly why these shares are generally the last ones to sell.`
            : 'Shares held in a tax-shielded account are exempt from this and should generally be sold last.'}
        </div>
      </div>

      <section>
        <div className="section-head"><h2>Where is this money going?</h2></div>
        <div className="rows">
          {REASONS.map((r) => (
            <button key={r.value} className="row" onClick={() => setReason(r.value)}
                    style={reason === r.value
                      ? { borderColor: 'var(--accent-edge)', background: 'var(--accent-bg)' }
                      : undefined}>
              <div className="row-top">
                <span className="row-ticker" style={{ fontWeight: 500 }}>{r.label}</span>
                {reason === r.value ? <span className="row-right" aria-hidden="true">✓</span> : null}
              </div>
              <div className="row-meta"><span>{r.blurb}</span></div>
            </button>
          ))}
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="sell-at">When you sold</label>
          <input id="sell-at" className="input" type="date" max={todayIso()}
                 value={at} onChange={(e) => setAt(e.target.value)} />
          <div className="hint">
            {at === todayIso()
              ? 'Today unless you say otherwise.'
              : `Recorded on ${shortDate(at)}, not today.`}
          </div>
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <label htmlFor="sell-fee">Commission ({listingCcy})</label>
          <input id="sell-fee" className="input" data-mono="true" inputMode="decimal"
                 value={fee} onChange={(e) => setFee(e.target.value)} placeholder="0.00" />
          <div className="hint">
            What the broker charges to close it. It comes off the proceeds and off what this sale is
            recorded as having made, so the tax figures are what the trade actually produced.
          </div>
        </div>

        {reason === 'rotate' ? (
          <div className="card card-pad" style={{ marginTop: 8 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="dest">Into what?</label>
              <input id="dest" className="input" value={destination} autoCapitalize="characters"
                     placeholder="Ticker" onChange={(e) => setDestination(e.target.value)} />
              {/* Said at the moment it matters, not afterwards (§7.4) */}
              {rotatingIntoHotTheme ? (
                <div className="note" data-tone="crit" style={{ marginTop: 10 }}>
                  {destination.trim().toUpperCase()} sits in {rotatingIntoHotTheme.name}, which is
                  already {pct(rotatingIntoHotTheme.weight)} of the book against your
                  {' '}{pct(limits.themeLimit)} limit. This rotation makes that worse, not better.
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <button className="btn btn-primary btn-block" style={{ marginTop: 18 }}
              disabled={clamped <= 0 || saving || !isDay(at)} onClick={() => void commit()}>
        Record selling {clamped > 0 ? sharesOf(Number(clamped.toFixed(4)), p.ticker) : ''}
      </button>
      <p className="dim" style={{ fontSize: 11.5, textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>
        This only writes it down. Place the order at your broker first.
      </p>
    </div>
  );
}

function BeforeAfter({ k, before, after, flag }: { k: string; before: string; after: string; flag: boolean }) {
  return (
    <div className="ba-row">
      <span className="k">{k}</span>
      <span className="num dim">{before}</span>
      <span className="arrow" aria-label="becomes">→</span>
      <span className={`num ${flag ? 'crit' : ''}`} style={{ fontWeight: 600 }}>{after}</span>
    </div>
  );
}
